/**
 * Сотрудники: список с поиском слева, карточка человека справа.
 *
 * На телефоне — одна колонка: список, по нажатию карточка с «назад», как
 * очередь и накладная в «Накладных».
 */

import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { useContext } from 'react';
import { AddStaff } from './AddStaff';
import { MayEditTeam } from './access';
import { Empty, ErrorState, Loading } from '@shell/states';
import { Icon } from '../../icons';
import { navigate } from '../../router';
import { EmployeeCard, StaffCard } from './EmployeeCard';
import { LEVEL_NAMES, SERVICE_NAMES, SERVICE_ORDER, displayName, onCallNote } from './api';
import type { StaffMember, TeamUser, UsersScreen } from './api';

export function People({
  users,
  openId,
  openStaffId = null,
  meId,
}: {
  users: UseQueryResult<UsersScreen, ApiError>;
  openId: number | null;
  openStaffId?: number | null;
  meId: number;
}) {
  const [search, setSearch] = useState('');
  const mayEdit = useContext(MayEditTeam);

  if (users.isPending) return <Loading text="Загружаю сотрудников…" />;
  if (users.error) return <ErrorState error={users.error} onRetry={() => users.refetch()} />;

  const needle = search.trim().toLowerCase();
  const shown = users.data.users.filter(
    (user) =>
      !needle ||
      [user.full_name, user.position_name]
        .filter(Boolean)
        .some((value) => (value as string).toLowerCase().includes(needle)),
  );
  const open = users.data.users.find((user) => user.user_id === openId) ?? null;
  const staff = users.data.staff ?? [];
  const shownStaff = staff.filter(
    (member) =>
      !needle ||
      [member.full_name, member.position_name]
        .filter(Boolean)
        .some((value) => (value as string).toLowerCase().includes(needle)),
  );
  const openStaff = staff.find((member) => member.employee_id === openStaffId) ?? null;
  const anyOpen = openId !== null || openStaffId !== null;

  return (
    <div className="team-people">
      <section className="team-people__list" aria-label="Сотрудники">
        <label className="search">
          <Icon name="search" size={16} />
          <input
            className="search__input"
            type="search"
            placeholder="Имя или должность"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Поиск сотрудника"
          />
        </label>
        {mayEdit && <AddStaff positions={users.data.positions ?? []} />}
        {shown.length === 0 && shownStaff.length === 0 ? (
          <Empty
            title={needle ? 'Никого не нашлось' : 'Сотрудников пока нет'}
            text={
              needle
                ? 'Попробуйте другое имя.'
                : 'Заведите человека кнопкой выше — или он появится сам, когда напишет боту и подаст заявку.'
            }
          />
        ) : (
          <ul className="team-list">
            {shown.map((user) => (
              <PersonRow
                key={user.user_id}
                user={user}
                me={user.user_id === meId}
                selected={user.user_id === openId}
              />
            ))}
            {shownStaff.map((member) => (
              <StaffRow
                key={`staff-${member.employee_id}`}
                member={member}
                selected={member.employee_id === openStaffId}
              />
            ))}
          </ul>
        )}
      </section>

      <div className="team-people__card">
        {openStaffId !== null ? (
          openStaff === null ? (
            <Empty
              title="Сотрудник не найден"
              text="Возможно, он уже написал боту и появился в списке со своим входом."
            />
          ) : (
            <>
              <button type="button" className="back-link team-back" onClick={() => navigate('/team')}>
                <Icon name="back" size={16} /> К списку
              </button>
              <StaffCard
                key={openStaff.employee_id}
                member={openStaff}
                positions={users.data.positions}
              />
            </>
          )
        ) : !anyOpen ? (
          <div className="team-placeholder">
            <Icon name="team" size={28} />
            <p>Выберите сотрудника — откроется всё о нём: должность, вход в агенты, деньги и права.</p>
          </div>
        ) : open === null ? (
          <Empty title="Сотрудник не найден" text="Возможно, запись удалили. Вернитесь к списку." />
        ) : (
          <>
            <button type="button" className="back-link team-back" onClick={() => navigate('/team')}>
              <Icon name="back" size={16} /> К списку
            </button>
            <EmployeeCard
              key={open.user_id}
              user={open}
              me={open.user_id === meId}
              positions={users.data.positions}
            />
          </>
        )}
      </div>
    </div>
  );
}

function accessLine(user: TeamUser): string {
  if (user.is_host) return 'владелец системы — все агенты';
  const parts = SERVICE_ORDER.filter((service) => user.access[service]).map(
    (service) => `${SERVICE_NAMES[service]} — ${LEVEL_NAMES[user.access[service]] ?? user.access[service]}`,
  );
  return parts.length ? parts.join(' · ') : 'нет входа ни в один агент';
}

function PersonRow({ user, me, selected }: { user: TeamUser; me: boolean; selected: boolean }) {
  const fired = user.employee_status === 'fired';
  return (
    <li>
      <button
        type="button"
        className={`team-person${selected ? ' team-person--selected' : ''}`}
        aria-current={selected ? 'true' : undefined}
        onClick={() => navigate(`/team/people/${user.user_id}`)}
      >
        <span className="team-person__top">
          <span className="team-person__name">
            {displayName(user)}
            {me && <span className="team-faint"> (вы)</span>}
          </span>
          {fired && <span className="pill">уволен</span>}
        </span>
        <span className="team-person__position">
          {user.position_name ?? 'должность не назначена'}
        </span>
        <span className="team-person__access">{accessLine(user)}</span>
      </button>
    </li>
  );
}

function StaffRow({ member, selected }: { member: StaffMember; selected: boolean }) {
  return (
    <li>
      <button
        type="button"
        className={`team-person${selected ? ' team-person--selected' : ''}`}
        aria-current={selected ? 'true' : undefined}
        onClick={() => navigate(`/team/staff/${member.employee_id}`)}
      >
        <span className="team-person__top">
          <span className="team-person__name">{member.full_name}</span>
          <span className="pill">{member.on_call ? 'вызывной' : 'нет Telegram'}</span>
        </span>
        <span className="team-person__position">
          {member.position_name ?? 'должность не назначена'}
        </span>
        <span className="team-person__access">
          {onCallNote(member) ?? 'ещё не писал ботам — входа нет'}
        </span>
      </button>
    </li>
  );
}
